import { useMemo } from 'react';

import { useJobsQuery as useJobsListQuery } from '../jobs/useJobsQuery.js';

/**
 * Minimal jobs-list query for the invoice-form job selector.
 *
 * Plan 22-10 promoted the jobs room to a real SPA feature, so the canonical jobs-list query now
 * lives in `features/jobs/useJobsQuery.ts` (it hits the coarse `GET /api/v1/jobs`). This thin
 * adapter delegates to it and maps the camelCase `jobName` DTO field back to the snake_case
 * `job_name` shape the invoice form already renders — so the picker keeps working unchanged while
 * the jobs feature owns the query. Through `apiFetch` — no tenant id sent (SEC-11).
 */

export interface JobOption {
  id: number;
  job_name: string | null;
  client_name: string | null;
}

export function useJobsQuery(enabled: boolean) {
  const query = useJobsListQuery({}, enabled);
  const jobs = useMemo<JobOption[]>(
    () =>
      (query.data?.jobs ?? []).map((j) => ({
        id: j.id,
        job_name: j.jobName,
        client_name: j.clientName,
      })),
    [query.data],
  );
  return { ...query, data: query.data ? { ...query.data, jobs } : undefined };
}
